import { useEffect, useState } from "react";
import {
  ArrowToDownHeader,
  CheckMarkIcon,
  Logo,
  SwapIconHeader,
} from "../AssetComponents/Images";
import { PurpleButton } from "./Buttons/PurpleButton";
import { Popover } from "react-tiny-popover";
import { SelectionGroup, SelectionItem } from "./Selection/Selection";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setConnectModal } from "../ReduxSlices/ModalSlice";
import { truncateWalletAddress } from "./functions/Functions";
import { BlackButton } from "./Buttons/BlackButton";
import ThemeToggle from "./Buttons/ThemeToggle";
import CopyToClipboard from "react-copy-to-clipboard";
import { setDisconnect, setEthereum, setBech32Address } from "../ReduxSlices/AuthSlice";
import { getNibiruSDK } from "../config/network";
import { evmToBech32Address, bech32ToEvmAddress } from "../evm/services/EvmContract";
import { ConnectButton, useActiveAccount, useDisconnect } from "thirdweb/react";
import { createWallet, inAppWallet } from "thirdweb/wallets";
import { thiredWebClient, nibiruChain } from "./functions/Functions";
import keplrIcon from "../assets/images/Keplr_icon_ver.1.3_2.svg";
import { getLastMarketPath } from "../utils/lastMarket";
import MarketsIcon from "../assets/images/Perps/Markets.svg";
import VaultsIcon from "../assets/images/Perps/vaults.svg";
import TradesIcon from "../assets/images/Perps/trades.svg";
import { ChartCandlestick, Landmark, Menu, X } from 'lucide-react';
import { RedButton } from "./Buttons/RedButton";
import { WhiteButton } from "./Buttons/WhiteButton";
import DisconnectModal from "./Modal/DisconnectModal";
import RefCodesModal from "./Modal/RefCodesModal";
import { PointsWidget } from "./PointsWidget";
import BridgeModal from "./Bridge/BridgeModal";

const metamaskIcon = "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg";

const getWalletIcon = (walletType) => {
  switch (walletType) {
    case "keplr":
      return keplrIcon;
    case "metamask":
    case "MetaMask":
      return metamaskIcon;
    case "bybit":
    case "Bybit":
      return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRqB80t8g90uzbolr1TPrZ6_T30jzCnvNok6g&s";
    case "thirdweb":
      return "https://thirdweb.com/favicon.ico";
    default:
      return "";
  }
};

export const Header = () => {
  const [showConnect, setShowConnect] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showRefCodes, setShowRefCodes] = useState(false);
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [bridgeTab, setBridgeTab] = useState("deposit");
  
  const isConnected = useSelector((state) => state.auth.isConnected);
  const account = useSelector((state) => state.auth.account);
  const wallet = useSelector((state) => state.auth.wallet);
  const ethereum = useSelector((state) => state.auth.ethereum);
  const bech32Address = useSelector((state) => state.auth.bech32Address);
  const profile = useSelector((state) => state.auth.profile);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const [current, setCurrent] = useState(0);
  const nibiru = getNibiruSDK();

  const tryConnect = () => {
    if (current === 4) {
      if (!ethereum) dispatch(setConnectModal(true));
      else {
        dispatch(setEthereum(null));
      }
    } else dispatch(setConnectModal(true));
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const initiateCopy = async () => {
    await delay(2000);
    setCopied(false);
  };

  const handleDisconnect = async () => {
    try {
      // If Thirdweb wallet, use Thirdweb disconnect
      if (isThirdwebConnected && disconnectThirdweb) {
        await disconnectThirdweb();
      } else if (wallet === 'keplr' && window.keplr && nibiru?.chainId) {
        await window.keplr.disable(nibiru.chainId);
      }
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
    } finally {
      dispatch(setDisconnect());
    }
  };

  const handleMobileNavigation = (path, currentValue) => {
    navigate(path);
    setCurrent(currentValue);
    setShowMobileMenu(false);
  };

  useEffect(() => {
    if (isConnected) setShowConnect(false);
  }, [isConnected]);

  // Listen for bridge modal open events from other components (e.g. TradingForm USDC click)
  useEffect(() => {
    const handler = (e) => {
      setBridgeTab(e.detail?.tab || "deposit");
      setBridgeOpen(true);
    };
    window.addEventListener("open-bridge-modal", handler);
    return () => window.removeEventListener("open-bridge-modal", handler);
  }, []);

  // When a Cosmos wallet (Keplr/Leap) connects with nibi1... address,
  // compute the corresponding EVM address (pure math, no RPC)
  useEffect(() => {
    console.log("[Header] address resolution effect:", { account, ethereum, bech32Address, wallet });
    if (account?.startsWith("nibi")) {
      // Set bech32 = account itself for Cosmos wallets
      if (!bech32Address) dispatch(setBech32Address(account));
      // Compute corresponding EVM address (pure math, no RPC)
      const evmAddr = bech32ToEvmAddress(account);
      console.log("[Header] nibi account detected:", account, "→ evm:", evmAddr, "current ethereum:", ethereum);
      if (evmAddr && evmAddr !== ethereum) {
        dispatch(setEthereum(evmAddr));
      }
    }
    // When an EVM wallet connects, resolve bech32 if not already set
    if (account?.startsWith("0x") && !bech32Address) {
      evmToBech32Address(account)
        .then((addr) => {
          dispatch(setBech32Address(addr));
          window.__nibiru_bech32 = addr;
        })
        .catch(() => {});
    }
  }, [account, ethereum, bech32Address, dispatch]);

  useEffect(() => {
    if (copied) initiateCopy();
  }, [copied]);

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    
    // Update navigation state: Markets(0), Vaults(1), Trades(2)
    switch (pathParts[2]) {
      case "markets":
        setCurrent(0);
        break;
      case "vaults":
      case "deposits":
        setCurrent(1);
        break;
      case "trades":
        setCurrent(2);
        break;
      default:
        setCurrent(-1);
        break;
    }
  }, [location]);

  const activeAccount = useActiveAccount();
  const { disconnect: disconnectThirdweb } = useDisconnect();
  
  // Check if connected via Thirdweb
  const isThirdwebConnected = wallet === 'thirdweb' && activeAccount?.address;

  // Cosmos wallets (Keplr/Leap) cannot sign the LayerZero OFT flow — it goes
  // through `window.ethereum`. They still get the Bridge button, limited to the
  // two hand-off providers (Nibiru Bridge / Stargate), which are plain external
  // redirects and so work for any wallet.
  const isCosmosWallet = wallet === "keplr" || wallet === "leap";
  const canBridge = isThirdwebConnected || (isConnected && (isCosmosWallet || account?.startsWith("0x")));
  
  // Thirdweb wallet configuration (includes Coinbase as auth option and extension)
  const thirdwebWallets = [
    inAppWallet({
      auth: {
        options: ["email", "google", "apple", "facebook", "coinbase"],
      },
    }),
    createWallet("com.coinbase.wallet"), // Coinbase Wallet browser extension/app
  ];

  useEffect(() => {
    if (activeAccount?.address) {
      dispatch(setEthereum(activeAccount.address));
      // Resolve bech32 address in background
      evmToBech32Address(activeAccount.address)
        .then((addr) => {
          dispatch(setBech32Address(addr));
          window.__nibiru_bech32 = addr;
        })
        .catch((err) => console.warn("bech32 resolution failed:", err.message));
      // Redirect to perps page when MetaMask/Ethereum wallet connects
      if (!ethereum && !location.pathname.startsWith('/perps')) {
        navigate(getLastMarketPath());
      }
    } else if (wallet === 'thirdweb' || !wallet) {
      // Only clear ethereum if this was a Thirdweb wallet disconnecting,
      // not when Keplr/Leap is connected (they set ethereum via bech32 conversion)
      dispatch(setEthereum(null));
    }
  }, [activeAccount, wallet, location.pathname, dispatch, navigate]);

  return (
    <>
      <div className="relative">
        {current !== 4 ? (
          <>
            <div className="ce-app-header bg-white flex w-full items-center justify-between gap-3 h-[60px] px-[18px] md:grid md:grid-cols-[1fr_auto_1fr] md:justify-items-center relative z-[100]">
              {/* Logo */}
              <Logo
                onClick={() => {
                  navigate(getLastMarketPath());
                }}
                className="justify-self-start cursor-pointer"
              />

              {/* Desktop Navigation — a sunken pill rail holding one raised
                  item, the same segmented-control object as the chart's
                  interval switch and the table's tabs. The selected and
                  unselected items are the same width so the rail does not
                  re-measure as you move between them. */}
              <div className="hidden md:block">
                <SelectionGroup
                  className="ce-nav-rail h-[38px] flex items-center"
                  defaultItem={current}
                >
                  {/* Markets */}
                  <SelectionItem
                    SelectedItem={
                      <div className="ce-nav-item ce-nav-item-on">
                        <img src={MarketsIcon} alt="Markets" className="ce-nav-icon w-[15px] h-[15px]" />
                        <div className="ce-nav-label">Markets</div>
                      </div>
                    }
                    UnselectedItem={
                      <div className="ce-nav-item">
                        <img src={MarketsIcon} alt="Markets" className="ce-nav-icon w-[15px] h-[15px] opacity-40" />
                        <div className="ce-nav-label">Markets</div>
                      </div>
                    }
                    onItemClick={() => {
                      navigate(getLastMarketPath());
                      setCurrent(0);
                    }}
                  />

                  {/* Vaults */}
                  <SelectionItem
                    SelectedItem={
                      <div className="ce-nav-item ce-nav-item-on">
                        <img src={VaultsIcon} alt="Liquidity" className="ce-nav-icon w-[15px] h-[15px]" />
                        <div className="ce-nav-label">Liquidity</div>
                      </div>
                    }
                    UnselectedItem={
                      <div className="ce-nav-item">
                        <img src={VaultsIcon} alt="Liquidity" className="ce-nav-icon w-[15px] h-[15px] opacity-40" />
                        <div className="ce-nav-label">Liquidity</div>
                      </div>
                    }
                    onItemClick={() => {
                      navigate("/perps/vaults");
                      setCurrent(1);
                    }}
                  />

                  {/* Portfolio - Only show when connected */}
                  {isConnected && (
                    <SelectionItem
                      SelectedItem={
                        <div className="ce-nav-item ce-nav-item-on">
                          <img src={TradesIcon} alt="Portfolio" className="ce-nav-icon w-[15px] h-[15px]" />
                          <div className="ce-nav-label whitespace-nowrap">Portfolio</div>
                        </div>
                      }
                      UnselectedItem={
                        <div className="ce-nav-item">
                          <img src={TradesIcon} alt="Portfolio" className="ce-nav-icon w-[15px] h-[15px] opacity-40" />
                          <div className="ce-nav-label whitespace-nowrap">Portfolio</div>
                        </div>
                      }
                      onItemClick={() => {
                        navigate("/perps/trades");
                        setCurrent(2);
                      }}
                    />
                  )}
                </SelectionGroup>
              </div>

              <div className="flex items-center flex-nowrap justify-self-end">
                {/* Swap Icon - Desktop only - Coming Soon
                <div
                  data-tooltip-id="my-tooltip"
                  className="hidden md:block mr-[20px] hover:bg-[#f6f6f6] hover:translate-y-[-4px] cursor-pointer bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-[10px] rounded-full"
                >
                  <SwapIconHeader />
                </div> */}

                {/* Mobile Menu Toggle */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMobileMenu(!showMobileMenu);
                  }}
                  className="ce-icon-btn md:hidden mr-4"
                >
                  {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
                </div>

                {/* Light/dark toggle (ADDENDUM-8 §3). It lives here rather than
                    in the trading top bar because this header is on every page
                    and the top bar's stat row has no room for it. The Pokédex
                    is excluded for free: MainLayouts swaps this header out for
                    CockpitHeader on the cockpit route, and that view keeps its
                    own casing colours. */}
                <div className="ce-raw mr-2">
                  <ThemeToggle style={{ width: 36, height: 36, fontSize: 15 }} />
                </div>

                {/* Points Widget - Desktop */}
                <div className="hidden md:block mr-2">
                  <PointsWidget />
                </div>

                {/* Desktop Ref Codes Button - only when connected */}
                {isConnected && (
                  <div
                    className="hidden md:block mr-2"
                    onClick={() => setShowRefCodes(true)}
                  >
                    <WhiteButton
                      text={
                        <span className="flex items-center gap-1.5 text-[13px] whitespace-nowrap">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                          Referral
                        </span>
                      }
                    />
                  </div>
                )}

                {/* Desktop Bridge Button — any connected wallet. Cosmos wallets
                    get the redirect-only providers (see `cosmosOnly` on BridgeModal). */}
                {canBridge && (
                  <div className="hidden md:block mr-2">
                    <WhiteButton
                      text={
                        <span className="flex items-center gap-1.5 text-[13px] whitespace-nowrap">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 16V4m0 0L3 8m4-4l4 4" />
                            <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                          Bridge
                        </span>
                      }
                      onClick={() => {
                        setBridgeTab("deposit");
                        setBridgeOpen(true);
                      }}
                    />
                  </div>
                )}

                {/* Desktop Leaderboard Button - show when NOT connected */}
                {!isConnected && !isThirdwebConnected && (
                  <div className="hidden md:block mr-2">
                    <WhiteButton
                      text={
                        <span className="flex items-center gap-1.5 text-[13px] whitespace-nowrap">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                            <path d="M4 22h16" />
                            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                          </svg>
                          Leaderboard
                        </span>
                      }
                      onClick={() => navigate("/perps/leaderboard")}
                    />
                  </div>
                )}

                {/* Desktop Connect Wallet */}
                <div className="hidden md:block">
                  {isThirdwebConnected ? (
                    // Show Thirdweb ConnectButton menu when connected via Thirdweb
                    <ConnectButton
                      client={thiredWebClient}
                      chain={nibiruChain}
                      wallets={thirdwebWallets}
                      theme="light"
                      connectButton={{
                        label: "Connect Wallet",
                        className: "thirdwebbutton hover:shadow-[-1px_6px_10px_0_rgba(93,0,207,0.5)] hover:translate-y-[-3px]",
                        style: { color: "#ffffff" },
                      }}
                      detailsButton={{
                        className: "thirdwebbutton",
                        style: { color: "#ffffff" },
                      }}
                    />
                  ) : (
                    // Show custom disconnect popover for MetaMask/Keplr/Leap
                    <Popover
                      isOpen={showDisconnectModal && window.innerWidth >= 768}
                      onClickOutside={() => {
                        if (window.innerWidth >= 768) setShowDisconnectModal(false);
                      }}
                      positions={["bottom"]}
                      align="end"
                      padding={8}
                      containerStyle={{ zIndex: 99999999 }}
                      content={
                        <DisconnectModal
                          account={account}
                          bech32Address={bech32Address}
                          evmAddress={ethereum}
                          walletIcon={getWalletIcon(wallet)}
                          onDisconnect={() => {
                            handleDisconnect();
                            setShowDisconnectModal(false);
                          }}
                        />
                      }
                    >
                      <div
                        className="w-max h-max"
                        onClick={() => {
                          if (isConnected) setShowDisconnectModal(!showDisconnectModal);
                          else tryConnect();
                        }}
                      >
                        <PurpleButton
                          text={
                            isConnected
                              ? truncateWalletAddress(account)
                              : "Connect Wallet"
                          }
                        />
                      </div>
                    </Popover>
                  )}
                </div>
              </div>
            </div>

            {/* MOBILE MENU OVERLAY */}
            {showMobileMenu && (
              <div className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg z-[101] border-t">
                <div className="p-4 space-y-4">
                  {/* Mobile Navigation Links */}
                  <div className="border-l-2 border-[#f0f0f0] pl-4">
                    <div className="text-[#666666] font-medium mb-2">Trade</div>
                    <div
                      onClick={() => handleMobileNavigation(getLastMarketPath(), 0)}
                      className={`cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2 ${current === 0 ? 'bg-[#f0f0f0] font-semibold' : ''}`}
                    >
                      <ChartCandlestick size={16} className="text-[#666666]"/>
                      <div className="text-[#666666]">Markets</div>
                    </div>
                    <div
                      onClick={() => handleMobileNavigation("/perps/vaults", 1)}
                      className={`cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2 ${current === 1 ? 'bg-[#f0f0f0] font-semibold' : ''}`}
                    >
                      <Landmark size={16} className="text-[#666666]"/>
                      <div className="text-[#666666]">Liquidity</div>
                    </div>
                    <div
                      onClick={() => { navigate("/perps/leaderboard"); setShowMobileMenu(false); }}
                      className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                        <path d="M4 22h16" />
                        <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                        <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                        <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                      </svg>
                      <div className="text-[#666666]">Leaderboard</div>
                    </div>
                    {isConnected && (
                      <>
                        <div
                          onClick={() => handleMobileNavigation("/perps/trades", 2)}
                          className={`cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2 ${current === 2 ? 'bg-[#f0f0f0] font-semibold' : ''}`}
                        >
                          <ChartCandlestick size={16} className="text-[#666666]"/>
                          <div className="text-[#666666]">Portfolio</div>
                        </div>
                        <div
                          onClick={() => { setShowRefCodes(true); setShowMobileMenu(false); }}
                          className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                          <div className="text-[#666666]">Ref Codes</div>
                        </div>
                        {canBridge && (
                          <div
                            onClick={() => {
                              setBridgeTab("deposit");
                              setBridgeOpen(true);
                              setShowMobileMenu(false);
                            }}
                            className="cursor-pointer p-2 rounded-lg hover:bg-[#f6f6f6] flex items-center gap-2"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M7 16V4m0 0L3 8m4-4l4 4" />
                              <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                            <div className="text-[#666666]">Bridge</div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Mobile Connect Wallet */}
                  <div className="border-t pt-4">
                    {isThirdwebConnected ? (
                      // Show Thirdweb ConnectButton for mobile when connected via Thirdweb
                      <ConnectButton
                        client={thiredWebClient}
                        chain={nibiruChain}
                        wallets={thirdwebWallets}
                        theme="light"
                        connectButton={{
                          label: "Connect Wallet",
                          className: "thirdwebbutton hover:shadow-[-1px_6px_10px_0_rgba(93,0,207,0.5)] hover:translate-y-[-3px] w-full",
                        }}
                        detailsButton={{
                          className: "thirdwebbutton w-full",
                        }}
                      />
                    ) : (
                      <>
                        <div
                          onClick={() => {
                            if (isConnected) {
                              setShowDisconnectModal(true);
                              setShowMobileMenu(false);
                            } else {
                              tryConnect();
                              setShowMobileMenu(false);
                            }
                          }}
                          className="w-full"
                        >
                          <PurpleButton
                            className="w-full"
                            text={
                              isConnected
                                ? truncateWalletAddress(account)
                                : "Connect Wallet"
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Disconnect Modal Overlay */}
            {showDisconnectModal && !isThirdwebConnected && (
              <div
                className="md:hidden fixed inset-0 z-[99999] flex items-center justify-center"
                onClick={() => setShowDisconnectModal(false)}
              >
                <div className="absolute inset-0 bg-black/30" />
                <div onClick={(e) => e.stopPropagation()} className="relative z-10">
                  <DisconnectModal
                    account={account}
                    bech32Address={bech32Address}
                    evmAddress={ethereum}
                    walletIcon={getWalletIcon(wallet)}
                    onDisconnect={() => {
                      handleDisconnect();
                      setShowDisconnectModal(false);
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="fixed w-full bg-white z-[100]">
            <div className="flex items-center justify-between py-[14px] px-[16px]">
              <Logo
                className="justify-self-start cursor-pointer"
                onClick={() => navigate(getLastMarketPath())}
              />
              <div className="flex items-center gap-2">
                {/* Bridge button — any connected wallet, Cosmos included. */}
                {(canBridge || ethereum) && (
                  <WhiteButton
                    text={
                      <span className="flex items-center gap-1.5 text-[13px] whitespace-nowrap">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 16V4m0 0L3 8m4-4l4 4" />
                          <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        Bridge
                      </span>
                    }
                    onClick={() => {
                      setBridgeTab("deposit");
                      setBridgeOpen(true);
                    }}
                  />
                )}
                <ConnectButton
                  theme="light"
                  connectButton={{
                    label: "Connect wallet",
                    className:
                      "thirdwebbutton hover:shadow-[-1px_6px_10px_0_rgba(93,0,207,0.5)] hover:translate-y-[-3px]",
                  }}
                  detailsButton={{
                    render() {
                      return (
                        <PurpleButton text={truncateWalletAddress(ethereum)} />
                      );
                    },
                  }}
                  client={thiredWebClient}
                  wallets={[createWallet("com.bybit"), createWallet("io.metamask")]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ref Codes Modal */}
      <RefCodesModal open={showRefCodes} onClose={() => setShowRefCodes(false)} />

      {/* Bridge Deposit/Withdraw Modal */}
      <BridgeModal
        open={bridgeOpen}
        onClose={() => setBridgeOpen(false)}
        walletAddress={ethereum || activeAccount?.address || (account?.startsWith("0x") ? account : null)}
        initialTab={bridgeTab}
        cosmosOnly={isCosmosWallet}
      />
    </>
  );
};